<template>
  <div class="app-cancellation-form">
    <slot name="txtBeforee"></slot>
    <div class="container-box">
      <form>
        <div class="content-limiter">
          <h2 class="text-3xl">Kündigungsformular</h2>
          <!-- Personal Data -->
          <fieldset>
            <legend class="text-xl visually-hidden">Persönliche Daten</legend>
            <div class="inputs-container">
              <div class="input-field">
                <h2 class="visually-hidden">Persönliche Daten</h2>

                <label class="text-base-medium" for="firstName">
                  Vorname
                </label>
                <ValidationIndicator
                  :dirty="$v.cancellation.customer.firstName.$dirty && !!cancellation.customer.firstName"
                  :valid="true"
                >
                  <input
                    id="firstName"
                    name="firstName"
                    placeholder="Max"
                    type="text"
                    class="input"
                    v-model="cancellation.customer.firstName"
                    @blur="$v.cancellation.customer.firstName.$touch"
                    :class="{
                      'input--valid': $v.cancellation.customer.firstName.$dirty && !!cancellation.customer.firstName
                    }"
                  />
                </ValidationIndicator>
              </div>
              <div class="input-field">
                <label class="text-base-medium" for="lastName">
                  Nachname
                </label>
                <ValidationIndicator
                  :dirty="$v.cancellation.customer.lastName.$dirty && !!cancellation.customer.lastName"
                  :valid="true"
                >
                  <input
                    id="lastName"
                    name="lastName"
                    placeholder="Mustermann"
                    type="text"
                    class="input"
                    v-model="cancellation.customer.lastName"
                    @blur="$v.cancellation.customer.lastName.$touch"
                    :class="{
                      'input--valid': $v.cancellation.customer.lastName.$dirty && !!cancellation.customer.lastName
                    }"
                  />
                </ValidationIndicator>
              </div>
            </div>
          </fieldset>
          <!-- Address Data -->
          <fieldset>
            <legend class="text-xl visually-hidden">Adressdaten</legend>
            <div class="inputs-container">
              <h2 class="visually-hidden">Adressdaten</h2>
              <div class="input-group">
                <div class="input-field input-field--fixed">
                  <label class="text-base-medium" for="street">
                    Straße
                  </label>
                  <ValidationIndicator
                    :dirty="$v.cancellation.address.street.$dirty && !!cancellation.address.street"
                    :valid="true"
                  >
                    <input
                      id="street"
                      name="street"
                      placeholder="Musterstr."
                      type="text"
                      class="input"
                      v-model="cancellation.address.street"
                      @blur="$v.cancellation.address.street.$touch"
                      :class="{
                        'input--valid': $v.cancellation.address.street.$dirty && !!cancellation.address.street
                      }"
                    />
                  </ValidationIndicator>
                </div>

                <div class="input-field input-field--fluid">
                  <label class="text-base-medium" for="houseNumber">
                    Hausnummer
                  </label>
                  <ValidationIndicator
                    :dirty="$v.cancellation.address.houseNumber.$dirty && !!cancellation.address.houseNumber"
                    :valid="true"
                  >
                    <input
                      id="houseNumber"
                      name="houseNumber"
                      placeholder="1a"
                      type="text"
                      class="input"
                      v-model="cancellation.address.houseNumber"
                      @blur="$v.cancellation.address.houseNumber.$touch"
                      :class="{
                        'input--valid': $v.cancellation.address.houseNumber.$dirty && !!cancellation.address.houseNumber
                      }"
                    />
                  </ValidationIndicator>
                </div>
              </div>
              <div class="input-group">
                <div class="input-field input-field--fluid">
                  <label class="text-base-medium" for="zip">
                    PLZ
                  </label>
                  <ValidationIndicator
                    :dirty="$v.cancellation.address.zip.$dirty && !!cancellation.address.zip"
                    :valid="isValid('zip', 'address', 'cancellation') || !cancellation.address.zip"
                  >
                    <input
                      id="zip"
                      name="zip"
                      placeholder="12345"
                      type="number"
                      class="input"
                      v-model="cancellation.address.zip"
                      @blur="$v.cancellation.address.zip.$touch"
                      @click="$v.cancellation.address.zip.$reset"
                      :class="{
                        'input--invalid':
                          $v.cancellation.address.zip.$dirty &&
                          !isValid('zip', 'address', 'cancellation') &&
                          !!cancellation.address.zip,
                        'input--valid': $v.cancellation.address.zip.$dirty && isValid('zip', 'address', 'cancellation')
                      }"
                      aria-errormessage="zip-error"
                    />
                  </ValidationIndicator>
                  <div
                    id="zip-error"
                    class="error-feedback"
                    v-if="
                      !isValid('zip', 'address', 'cancellation') &&
                        !!cancellation.address.zip &&
                        $v.cancellation.address.zip.$dirty
                    "
                  >
                    <span>Die Postleitzahl darf nur aus Zahlen bestehen und muss fünfstellig sein. </span>
                  </div>
                </div>

                <div class="input-field input-field--fixed">
                  <label class="text-base-medium" for="houseNumber">
                    Ort
                  </label>
                  <ValidationIndicator
                    :dirty="$v.cancellation.address.city.$dirty && !!cancellation.address.city"
                    :valid="true"
                  >
                    <input
                      id="city"
                      name="city"
                      placeholder="Musterstadt"
                      type="text"
                      class="input"
                      v-model="cancellation.address.city"
                      @blur="$v.cancellation.address.city.$touch"
                      :class="{
                        'input--valid': $v.cancellation.address.city.$dirty && !!cancellation.address.city
                      }"
                    />
                  </ValidationIndicator>
                </div>
              </div>
            </div>
          </fieldset>
          <fieldset>
            <legend class="text-xl visually-hidden">Kontaktdaten</legend>
            <div class="inputs-container">
              <h2 class="visually-hidden">Kontaktdaten</h2>
              <div class="input-field">
                <label class="text-base-medium" for="email">
                  E-Mail-Adresse
                </label>
                <ValidationIndicator :dirty="$v.cancellation.email.$dirty && !!cancellation.email" :valid="true">
                  <input
                    id="email"
                    name="email"
                    placeholder="max.mustermannn@muster.de"
                    type="email"
                    class="input"
                    @blur="$v.cancellation.email.$touch"
                    :class="{
                      'input--valid': $v.cancellation.email.$dirty && !!cancellation.email
                    }"
                    v-model="cancellation.email"
                  />
                </ValidationIndicator>
              </div>
              <div class="input-field">
                <label class="text-base-medium" for="customerID">
                  Kundennummer
                </label>
                <ValidationIndicator
                  :dirty="$v.cancellation.customer.customerID.$dirty && !!cancellation.customer.customerID"
                  :valid="true"
                >
                  <input
                    id="customerID"
                    name="customerID"
                    placeholder="123456789"
                    type="number"
                    class="input"
                    @blur="$v.cancellation.customer.customerID.$touch"
                    :class="{
                      'input--valid': $v.cancellation.customer.customerID.$dirty && !!cancellation.customer.customerID
                    }"
                    v-model="cancellation.customer.customerID"
                  />
                </ValidationIndicator>
              </div>
              <div class="input-field">
                <label class="text-base-medium" for="contractID">
                  Vertragsnummer
                </label>
                <ValidationIndicator
                  :dirty="$v.cancellation.customer.contractID.$dirty && !!cancellation.customer.contractID"
                  :valid="true"
                >
                  <input
                    id="contractID"
                    name="contractID"
                    placeholder="123456789"
                    type="number"
                    class="input"
                    @blur="$v.cancellation.customer.contractID.$touch"
                    :class="{
                      'input--valid': $v.cancellation.customer.contractID.$dirty && !!cancellation.customer.contractID
                    }"
                    v-model="cancellation.customer.contractID"
                  />
                </ValidationIndicator>
              </div>
            </div>
          </fieldset>
          <fieldset>
            <legend class="text-xl visually-hidden">Details</legend>
            <div class="inputs-container">
              <div class="input-field">
                <h2 class="visually-hidden">Details</h2>
                <label class="text-base-medium" for="preferredCancellationDate">
                  Gewünschter Kündigungstermin
                </label>
                <ValidationIndicator
                  :dirty="$v.cancellation.preferredCancellationDate.$dirty"
                  :valid="!$v.cancellation.preferredCancellationDate.$invalid"
                >
                  <input
                    id="preferredCancellationDate"
                    name="preferredCancellationDate"
                    placeholder="frühstmöglich"
                    type="text"
                    class="input"
                    v-model="cancellation.preferredCancellationDate"
                    @input="$v.cancellation.preferredCancellationDate.$touch"
                    :class="{
                      'input--valid':
                        $v.cancellation.preferredCancellationDate.$dirty && isValid('preferredCancellationDate', 'cancellation')
                    }"
                    @blur="updateEmptyCancellationDate"
                    aria-errormessage="preferredCancellationDate-error"
                  />
                </ValidationIndicator>
                <div id="preferredCancellationDate-info">
                  <p class="text-sm text-muted">
                    Ist dein gewünschter Kündigungszeitpunkt ein anderer als frühestmöglich, trage hier dein gewünschtes Datum
                    ein.
                  </p>
                  <p class="sr-only">
                    Bitte gebe das Datum im Format <strong>TT.MM.JJJJ</strong> ein. Zwei Ziffern für den Tag, Punkt, zwei
                    Ziffern für den Monat, Punkt, vier Ziffern für das Jahr.
                  </p>
                </div>
                <div
                  id="preferredCancellationDate-error"
                  class="error-feedback"
                  v-if="
                    !isValid('preferredCancellationDate', 'cancellation') && $v.cancellation.preferredCancellationDate.$dirty
                  "
                >
                  <span>
                    Ungültiges Format. Bitte gib ein Datum im Format TT.MM.JJJJ ein.
                  </span>
                </div>
              </div>
              <div class="input-field">
                <h2 class="visually-hidden">Details</h2>
                <label class="text-base-medium" for="details">
                  Details zur Kündigung
                </label>
                <ValidationIndicator :dirty="$v.cancellation.details.$dirty && !!cancellation.details" :valid="true">
                  <input
                    id="details"
                    name="details"
                    placeholder=""
                    type="text"
                    class="input"
                    v-model="cancellation.details"
                    @blur="$v.cancellation.details.$touch"
                    :class="{
                      'input--valid': $v.cancellation.details.$dirty && !!cancellation.details
                    }"
                  />
                </ValidationIndicator>
                <div id="details-info">
                  <p class="text-sm text-muted">
                    Teile uns gerne mit, welches Produkt <abbr title="zum Beispiel">z.B.</abbr> welchen Tarif oder welche Option du genau kündigen möchtest und
                    was der Grund deiner Kündigung ist.
                  </p>
                </div>
              </div>
            </div>
          </fieldset>
        </div>
      </form>
    </div>
    <div class="container-box">
      <div class="content-limiter">
        <captcha @captchaCode="updateCaptchaData" aria-errormessage="captcha-error" ref="captcha"> </captcha>
        <div class="input-field">
          <div
            id="captcha-error"
            class="error-feedback"
            v-if="cancellation.captcha.captchaCode == null && $v.cancellation.captcha.$dirty"
          >
            <span>Bitte bestätige den Captcha</span>
          </div>
        </div>
      </div>
      <div></div>
    </div>
    <slot name="txtAfter"></slot>
    <Stepper
      class="justify-end"
      @next="save()"
      :loading-next="loading"
      :show-prev="false"
      :disabled-next="loading"
      :text-next="'Jetzt kündigen'"
    >
      <template v-for="slot in possibleChildSlotsFor('Stepper')" v-slot:[`${slot.child}`]>
        <slot :name="`${slot.parent}`"></slot>
      </template>
    </Stepper>
  </div>
</template>

<script>
import isValid from '@/mixins/isValid'
import { validationMixin } from 'vuelidate'
import { email, helpers, maxLength } from 'vuelidate/lib/validators'
import { validZipCode, validCityString } from '@/validators/address'
import Stepper from '@/components-v2/generic/Stepper.vue'
import slotProxy from '@/mixins/slotProxy'
import { mapActions, mapMutations } from 'vuex'
import { stepName as nextStep } from './CancellationSuccess.vue'
import Captcha from '@/components-v2/flows/LIDL/Cancellation/Captcha.vue'
import ValidationIndicator from '@/components-v2/generic/ValidationIndicator.vue'

const alphaNumAndDSpecialChars = helpers.regex('alphaNumAndDSpecialChars', /^[a-zA-Z0-9äöüÄÖÜß/. ,-]*$/)
const numAndDSpecialChars = helpers.regex('numAndDSpecialChars', /^[0-9/.,-]*$/)
const date = helpers.regex(
  'date',
  /^(([0]?[1-9]|[1|2][0-9]|[3][0|1])[./-]([0]?[1-9]|[1][0-2])[./-]([0-9]{4}|[0-9]{2})|(frühstmöglich))$/
)

export const stepName = 'cancellationForm'

export default {
  name: 'CancellationForm',
  components: {
    Captcha,
    Stepper,
    ValidationIndicator
  },
  mixins: [isValid, validationMixin, slotProxy()],
  data() {
    return {
      cancellation: {
        address: {
          street: null,
          houseNumber: null,
          zip: null,
          city: null
        },
        email: null,
        customer: {
          firstName: null,
          lastName: null,
          customerID: null,
          contractID: null
        },
        captcha: {
          captchaToken: null,
          captchaCode: null
        },
        preferredCancellationDate: 'frühstmöglich',
        details: null
      },
      loading: false
    }
  },
  methods: {
    ...mapActions('cancel', ['confirmCancellation']),
    ...mapMutations('cancel', ['changeStep']),
    save() {
      if (this.loading) return
      this.loading = true
      this.$v.cancellation.captcha.$touch()
      if (
        (this.cancellation.captcha.captchaCode == null || this.cancellation.captcha.captchaCode === '') &&
        this.$v.cancellation.captcha.$dirty
      ) {
        this.$refs.captcha.error = true
        this.cancellation.captcha.captchaCode = null
      }

      if (this.$v.$invalid) {
        this.$nextTick(() => {
          const firstError = document.querySelector('error-feedback')
          if (firstError) firstError.focus()
        })
      }

      if (this.oneFieldFilled) {
        this.confirmCancellation({
          cancellationData: this.cancellation,
          nextStep: nextStep
        }).then(() => {
          this.loading = false
        })
      } else {
        this.loading = false
      }
    },
    updateCaptchaData(captchaData) {
      this.$v.cancellation.captcha.$touch()
      this.cancellation.captcha = captchaData
      this.$refs.captcha.error = false
    },
    updateEmptyCancellationDate() {
      if (this.cancellation.preferredCancellationDate === '') {
        this.cancellation.preferredCancellationDate = 'frühstmöglich'
      }
    }
  },
  computed: {
    oneFieldFilled() {
      if (this.cancellation.captcha.captchaCode !== null && this.cancellation.captcha.captchaCode !== '') {
        if (this.cancellation.customer.firstName !== null && this.cancellation.customer.firstName !== '') {
          return true
        }
        if (this.cancellation.customer.lastName !== null && this.cancellation.customer.lastName !== '') {
          return true
        }
        if (this.cancellation.customer.customerID !== null && this.cancellation.customer.customerID !== '') {
          return true
        }
        if (this.cancellation.customer.contractID !== null && this.cancellation.customer.contractID !== '') {
          return true
        }
        if (this.cancellation.email !== null && this.cancellation.email !== '') {
          return true
        }
        if (
          this.cancellation.preferredCancellationDate !== null &&
          this.cancellation.preferredCancellationDate !== '' &&
          this.cancellation.preferredCancellationDate === 'frühstmöglich'
        ) {
          return true
        }
        if (this.cancellation.details !== null && this.cancellation.details !== '') {
          return true
        }
        if (this.cancellation.address.street !== null && this.cancellation.address.street !== '') {
          return true
        }
        if (this.cancellation.address.zip !== null && this.cancellation.address.zip !== '') {
          return true
        }
        if (this.cancellation.address.city !== null && this.cancellation.address.city !== '') {
          return true
        }
        return this.cancellation.address.houseNumber !== null && this.cancellation.address.houseNumber !== ''
      } else {
        return false
      }
    }
  },
  validations() {
    return {
      cancellation: {
        address: {
          street: {
            alphaNumAndDSpecialChars
          },
          houseNumber: {
            alphaNumAndDSpecialChars
          },
          zip: {
            validZipCode
          },
          city: {
            validCityString
          }
        },
        email: {
          email
        },
        customer: {
          firstName: {},
          lastName: {},
          customerID: {
            numAndDSpecialChars,
            maxLength: maxLength(70)
          },
          contractID: {
            numAndDSpecialChars,
            maxLength: maxLength(70)
          }
        },
        preferredCancellationDate: {
          date
        },
        details: {
          maxLength: maxLength(800)
        },
        captcha: {}
      }
    }
  }
}
</script>
